import { useState, useEffect, useCallback } from 'react';
import { Logo } from '../components/Logo';
import './Install.css';
import '../pages/admin/Admin.css';

export type InstallProps = {
  /** true when rendered under /admin/install (sidebar layout). */
  embedded?: boolean;
};

function InstallLayout({
  embedded,
  title,
  lead,
  children,
}: {
  embedded?: boolean;
  title: string;
  lead: string;
  children: React.ReactNode;
}) {
  if (embedded) {
    return (
      <div className="admin-page install-page install-embedded">
        <div className="install-page-inner">
          <header className="admin-page-header">
            <div>
              <h1>{title}</h1>
              <p>{lead}</p>
            </div>
          </header>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="install-page">
      <div className="install-page-inner">
        <div className="install-page-brand">
          <Logo className="huge" />
        </div>
        <h1 className="install-page-title">{title}</h1>
        <p className="install-page-lead">{lead}</p>
        {children}
      </div>
    </div>
  );
}

interface InstallStatus {
  ready: boolean;
  needsInstall: boolean;
  message: string;
}

interface InstallResultDto {
  scriptName: string;
  success: boolean;
  errorMessage?: string;
}

interface InstallResponse {
  success: boolean;
  results: InstallResultDto[];
}

interface SqlMigrationItem {
  name: string;
  applied: boolean | null;
  appliedAt?: string | null;
}

interface SqlMigrationsResponse {
  databaseReachable: boolean;
  message?: string | null;
  migrations: SqlMigrationItem[];
  pendingCount: number | null;
}

async function readJsonResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') ?? '';
  const responseText = await res.text();

  if (!contentType.toLowerCase().includes('application/json')) {
    const preview = responseText.slice(0, 200).replace(/\s+/g, ' ').trim();
    throw new Error(`Expected JSON but got ${contentType || 'unknown'} (HTTP ${res.status}). Body: ${preview}`);
  }

  if (!res.ok) {
    const preview = responseText.slice(0, 200).replace(/\s+/g, ' ').trim();
    throw new Error(`API error (HTTP ${res.status}): ${preview}`);
  }

  return JSON.parse(responseText) as T;
}

export function Install({ embedded = false }: InstallProps) {
  const [installPassword, setInstallPassword] = useState<string>(() => sessionStorage.getItem('installPassword') ?? '');
  const [unlocked, setUnlocked] = useState(false);
  const [status, setStatus] = useState<InstallStatus | null>(null);
  const [migrationsData, setMigrationsData] = useState<SqlMigrationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [migrationsLoading, setMigrationsLoading] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [applyingMigrations, setApplyingMigrations] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const getInstallHeaders = useCallback((): HeadersInit => {
    return {
      'X-Install-Password': installPassword,
    };
  }, [installPassword]);

  const loadInstallPageData = useCallback(
    async (silent?: boolean) => {
      if (!unlocked) return;
      setLoading(true);
      setMigrationsLoading(true);
      try {
        const [statusRes, migRes] = await Promise.all([
          fetch('/api/install/status', { headers: getInstallHeaders() }),
          fetch('/api/install/migrations', { headers: getInstallHeaders() }),
        ]);
        const statusData = await readJsonResponse<InstallStatus>(statusRes);
        const migData = await readJsonResponse<SqlMigrationsResponse>(migRes);
        setStatus(statusData);
        setMigrationsData(migData);
        if (!silent) {
          setLog((prev) => [...prev, `[${new Date().toISOString()}] ${statusData.message}`]);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to reach API';
        setStatus({ ready: false, needsInstall: true, message: msg });
        setMigrationsData(null);
        setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
      } finally {
        setLoading(false);
        setMigrationsLoading(false);
      }
    },
    [getInstallHeaders, unlocked],
  );

  useEffect(() => {
    if (!unlocked) return;
    loadInstallPageData(false);
  }, [unlocked, loadInstallPageData]);

  const handleUnlock = async () => {
    if (!installPassword.trim()) {
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: Install password is required.`]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/install/unlock', {
        method: 'POST',
        headers: getInstallHeaders(),
      });
      await readJsonResponse<{ message?: string }>(res);
      sessionStorage.setItem('installPassword', installPassword);
      setUnlocked(true);
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Install access granted.`]);
    } catch (err) {
      sessionStorage.removeItem('installPassword');
      setUnlocked(false);
      const msg = err instanceof Error ? err.message : 'Invalid install password';
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async () => {
    setInstalling(true);
    setLog((prev) => [...prev, `[${new Date().toISOString()}] Starting installation...`]);
    try {
      const res = await fetch('/api/install', { method: 'POST', headers: getInstallHeaders() });
      const data = await readJsonResponse<InstallResponse>(res);
      for (const r of data.results) {
        const line = r.success
          ? `  ✓ ${r.scriptName}`
          : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
        setLog((prev) => [...prev, line]);
      }
      setLog((prev) => [
        ...prev,
        `[${new Date().toISOString()}] ${data.success ? 'Installation completed successfully.' : 'Installation completed with errors.'}`,
      ]);
      if (data.success) {
        await loadInstallPageData(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Install request failed';
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
    } finally {
      setInstalling(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setLog((prev) => [...prev, `[${new Date().toISOString()}] Starting seed...`]);
    try {
      const res = await fetch('/api/install/seed', { method: 'POST', headers: getInstallHeaders() });
      const data = await readJsonResponse<InstallResponse>(res);
      for (const r of data.results) {
        const line = r.success
          ? `  ✓ ${r.scriptName}`
          : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
        setLog((prev) => [...prev, line]);
      }
      setLog((prev) => [
        ...prev,
        `[${new Date().toISOString()}] ${data.success ? 'Seed completed successfully.' : 'Seed completed with errors.'}`,
      ]);
      await loadInstallPageData(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Seed request failed';
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
    } finally {
      setSeeding(false);
    }
  };

  const handleApplyMigrations = async () => {
    setApplyingMigrations(true);
    setLog((prev) => [...prev, `[${new Date().toISOString()}] Bekleyen SQL migration'ları uygulanıyor...`]);
    try {
      const res = await fetch('/api/install/migrations/apply', {
        method: 'POST',
        headers: getInstallHeaders(),
      });
      const data = await readJsonResponse<InstallResponse>(res);
      if (data.results.length === 0) {
        setLog((prev) => [...prev, `  (Bekleyen migration yok)`]);
      }
      for (const r of data.results) {
        const line = r.success
          ? `  ✓ ${r.scriptName}`
          : `  ✗ ${r.scriptName}: ${r.errorMessage ?? 'Unknown error'}`;
        setLog((prev) => [...prev, line]);
      }
      setLog((prev) => [
        ...prev,
        `[${new Date().toISOString()}] ${data.success ? "Migration'lar tamamlandı." : "Migration'lar hata ile bitti."}`,
      ]);
      await loadInstallPageData(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Migration isteği başarısız';
      setLog((prev) => [...prev, `[${new Date().toISOString()}] Error: ${msg}`]);
    } finally {
      setApplyingMigrations(false);
    }
  };

  const migrationSectionBusy = installing || seeding || applyingMigrations;
  const pendingMigrations =
    migrationsData?.databaseReachable === true
      ? migrationsData.migrations.filter((m) => m.applied === false).length
      : null;

  const renderMigrationsPanel = () => (
    <div className="install-panel install-migrations">
      <div className="install-migrations-header">
        <span className="install-migrations-title">SQL migration dosyaları</span>
        {migrationsLoading ? (
          <span className="install-migrations-meta">Yükleniyor…</span>
        ) : migrationsData ? (
          <span className="install-migrations-meta">
            {migrationsData.databaseReachable ? (
              <>
                Bekleyen: <strong>{pendingMigrations}</strong>
              </>
            ) : (
              <span className="install-migrations-warn">
                Veritabanına ulaşılamadı
                {migrationsData.message ? ` — ${migrationsData.message}` : ''}
              </span>
            )}
          </span>
        ) : null}
      </div>
      {migrationsData && migrationsData.migrations.length > 0 ? (
        <div className="install-migrations-table-wrap">
          <table className="install-migrations-table">
            <thead>
              <tr>
                <th>Dosya</th>
                <th>Durum</th>
                <th>Uygulanma</th>
              </tr>
            </thead>
            <tbody>
              {migrationsData.migrations.map((m) => (
                <tr key={m.name}>
                  <td className="install-migrations-name">{m.name}</td>
                  <td>
                    {m.applied === true && <span className="install-migrations-badge applied">Uygulandı</span>}
                    {m.applied === false && <span className="install-migrations-badge pending">Bekliyor</span>}
                    {m.applied === null && <span className="install-migrations-badge unknown">Bilinmiyor</span>}
                  </td>
                  <td className="install-migrations-date">
                    {m.appliedAt ? new Date(m.appliedAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !migrationsLoading && (
          <p className="install-migrations-empty">Tanımlı db.migration-*.sql dosyası yok.</p>
        )
      )}
      <div className="install-migrations-actions">
        <button
          type="button"
          className="install-btn install-btn-primary"
          onClick={handleApplyMigrations}
          disabled={
            migrationSectionBusy ||
            migrationsLoading ||
            !migrationsData?.databaseReachable ||
            pendingMigrations === 0
          }
        >
          {applyingMigrations ? 'Uygulanıyor…' : "Bekleyen migration'ları çalıştır"}
        </button>
      </div>
    </div>
  );

  const renderLogPanel = (emptyLabel = 'Waiting...') => (
    <div className="install-panel install-log-panel">
      <div className="install-log-container">
        <div className="install-log-header">Execution Log</div>
        <pre className="install-log">{log.length === 0 ? emptyLabel : log.join('\n')}</pre>
      </div>
    </div>
  );

  const renderMigrationsAndLog = (emptyLogLabel = 'Waiting...') => (
    <section className="install-section install-split">
      {renderMigrationsPanel()}
      {renderLogPanel(emptyLogLabel)}
    </section>
  );

  if (!unlocked) {
    return (
      <InstallLayout
        embedded={embedded}
        title="Install Access"
        lead="Kuruluma devam etmek için install şifresini girin."
      >
        <section className="install-section">
          <div className="install-field">
            <label className="install-label" htmlFor="install-password">
              Install password
            </label>
            <input
              id="install-password"
              type="password"
              value={installPassword}
              onChange={(e) => setInstallPassword(e.target.value)}
              className="install-input"
              placeholder="Install password"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleUnlock();
              }}
            />
          </div>
          <div className="install-actions">
            <button
              type="button" 
              className="install-btn install-btn-primary"
              onClick={handleUnlock}
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Unlock'}
            </button>
          </div>
        </section>
        {log.length > 0 && (
          <section className="install-section">{renderLogPanel()}</section>
        )}
      </InstallLayout>
    );
  }

  if (status?.ready) {
    return (
      <InstallLayout
        embedded={embedded}
        title="Database Ready"
        lead={status.message}
      >
        <section className="install-section">
          <h2 className="install-section-title">Seed</h2>
          <p className="install-section-text">Seed script’ini yeniden çalıştırabilirsiniz.</p>
          <div className="install-actions">
            <button
              type="button"
              className="install-btn install-btn-primary"
              onClick={handleSeed}
              disabled={seeding || applyingMigrations}
            >
              {seeding ? 'Seeding...' : 'Run Seed'}
            </button>
          </div>
        </section>
        {renderMigrationsAndLog()}
      </InstallLayout>
    );
  }

  return (
    <InstallLayout
      embedded={embedded}
      title="Database Setup"
      lead={loading ? 'Checking database...' : status?.message ?? 'Unknown status'}
    >
      {status?.needsInstall && !loading && (
        <section className="install-section">
          <h2 className="install-section-title">Kurulum</h2>
          <p className="install-section-text">
            Şema kurulumu ve seed işlemlerini buradan çalıştırın.
          </p>
          <div className="install-actions">
            <button
              type="button"
              className="install-btn install-btn-primary"
              onClick={handleInstall}
              disabled={migrationSectionBusy}
            >
              {installing ? 'Installing...' : 'Install Database'}
            </button>
            <button
              type="button"
              className="install-btn"
              onClick={handleSeed}
              disabled={migrationSectionBusy}
            >
              {seeding ? 'Seeding...' : 'Run Seed'}
            </button>
          </div>
        </section>
      )}

      {renderMigrationsAndLog()}
    </InstallLayout>
  );
}
